<!-- Receive Goods Page -->
<div data-testid="receive-goods-page" class="p-6 receive-goods-page">
  <!-- Header -->
  <div
    class="mb-6 pb-4 border-b border-gray-200 flex justify-between items-center"
  >
    <h2 class="text-2xl font-semibold text-gray-800">
      {{ "inbound.receive.receive_goods" | translate }}
    </h2>
    <div class="flex gap-2">
      <app-button
        [testId]="'receive-goods-cancel-btn'"
        label="common.actions.cancel"
        type="secondary"
        [outlined]="true"
        (emitOnClick)="onCancel()"
      ></app-button>
      <app-button
        [testId]="'receive-goods-confirm-btn'"
        label="common.actions.confirm"
        type="info"
        (emitOnClick)="onConfirm()"
        [disabled]="trackingLines.length === 0 || hasValidationErrors"
      ></app-button>
    </div>
  </div>

  <!-- Main Content: Two Columns -->
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <!-- Left Column: Product List (Read-only) -->
    <div class="lg:col-span-7 bg-white p-4">
      <div class="mb-4">
        <h4 class="text-lg font-semibold text-gray-800">
          {{ "inbound.sections.product_list" | translate }}
        </h4>
      </div>
      @if (productListDataSource.length === 0 && !loading) {
        <!-- Custom empty message when all items are received -->
        <div
          class="p-8 text-center bg-gray-50 border border-gray-200 rounded-lg"
        >
          <span
            class="material-symbols-outlined text-4xl text-green-500 mb-3 block"
            >check_circle</span
          >
          <p class="text-gray-600 font-medium">
            {{ "inbound.messages.all_items_received" | translate }}
          </p>
        </div>
      } @else {
        <div class="product-list-table">
          <app-data-table
            data-testid="receive-goods-product-list-table"
            [columns]="productListColumns"
            [dataSource]="productListDataSource"
            [loading]="loading"
            [hasCheckColumn]="false"
            [hasSerialColumn]="false"
            [totalRecords]="productListDataSource.length"
            [first]="0"
            [rows]="productListDataSource.length"
            [showPaginator]="false"
            [scrollHeight]="'400px'"
          >
          </app-data-table>
        </div>
      }
    </div>

    <!-- Right Column: Add Tracking Line Form -->
    <div class="lg:col-span-5 p-4">
      <!-- Add Tracking Line Form -->
      <div class="mb-4">
        <form
          data-testid="receive-goods-tracking-line-form"
          [formGroup]="addTrackingLineForm"
          class="space-y-3 add-tracking-line-form"
        >
          <!-- Barcode Input Row -->
          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ "inbound.receive.scan_barcode" | translate }}
            </label>
            <div class="w-full">
              <input
                data-testid="receive-goods-barcode-input"
                type="text"
                pInputText
                formControlName="barcode"
                [(ngModel)]="formBarcodeInput"
                [placeholder]="
                  'inbound.receive.barcode_placeholder' | translate
                "
                class="w-full text-lg py-3"
                (keyup.enter)="onFormBarcodeInput()"
              />
            </div>
          </div>

          <!-- No separate picker here — reuses the "Product Name" select below
               as the correction UI to avoid a duplicate dropdown. -->
          @if (pendingBarcodeCorrection) {
            <div
              data-testid="receive-goods-barcode-correction-panel"
              class="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg"
            >
              <p class="text-sm text-amber-800 mb-2">
                {{ pendingBarcodeCorrection.message }}
                {{ "inbound.receive.correct_barcode_select_label" | translate }}
              </p>
              <app-button
                [testId]="'receive-goods-barcode-correction-cancel-btn'"
                [label]="'inbound.receive.correct_barcode_cancel' | translate"
                type="secondary"
                [outlined]="true"
                (emitOnClick)="onCancelBarcodeCorrection()"
              ></app-button>
            </div>
          }

          <!-- First Row: Product Name and Quantity (7:3) -->
          <div class="grid grid-cols-10 gap-3 mb-3">
            <!-- Product Name Select -->
            <div class="col-span-6">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ "inbound.fields.product_name" | translate }}
              </label>
              <p-select
                data-testid="receive-goods-product-select"
                [options]="getProductOptions()"
                formControlName="product"
                [placeholder]="'inbound.receive.select_product' | translate"
                [showClear]="true"
                [filter]="true"
                filterBy="skuName,skuCode"
                [filterPlaceholder]="
                  'common.search.form.actions.search' | translate
                "
                [emptyFilterMessage]="
                  'common.messages.no_results_found' | translate
                "
                [emptyMessage]="'common.messages.no_results_found' | translate"
                styleClass="w-full"
                [class.p-invalid]="
                  addTrackingLineForm.get('product')?.invalid &&
                  addTrackingLineForm.get('product')?.touched
                "
              >
                <ng-template let-item pTemplate="item">
                  <div>{{ item.skuName || item.skuCode }}</div>
                </ng-template>
                <ng-template let-item pTemplate="selectedItem">
                  <div>{{ item.skuName || item.skuCode }}</div>
                </ng-template>
              </p-select>
              <small
                class="text-red-500 text-sm mt-1 block"
                [class.invisible]="
                  !(
                    addTrackingLineForm.get('product')?.invalid &&
                    addTrackingLineForm.get('product')?.touched
                  )
                "
                [style.min-height.rem]="1.25"
              >
                {{ "common.validation.required" | translate }}
              </small>
            </div>

            <!-- Quantity -->
            <div class="col-span-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ "inbound.fields.quantity" | translate }}
              </label>
              <input
                data-testid="receive-goods-quantity-input"
                type="number"
                pInputText
                formControlName="quantity"
                [min]="1"
                class="w-full"
                [class.p-invalid]="
                  addTrackingLineForm.get('quantity')?.invalid &&
                  addTrackingLineForm.get('quantity')?.touched
                "
              />
              <small
                class="text-red-500 text-sm mt-1 block"
                [class.invisible]="
                  !(
                    addTrackingLineForm.get('quantity')?.invalid &&
                    addTrackingLineForm.get('quantity')?.touched
                  )
                "
                [style.min-height.rem]="1.25"
              >
                @if (
                  addTrackingLineForm.get("quantity")?.invalid &&
                  addTrackingLineForm.get("quantity")?.touched
                ) {
                  @if (
                    addTrackingLineForm.get("quantity")?.errors?.["required"]
                  ) {
                    {{ "common.validation.required" | translate }}
                  } @else if (
                    addTrackingLineForm.get("quantity")?.errors?.["min"]
                  ) {
                    {{ "common.validation.min_value" | translate: { min: 1 } }}
                  } @else if (
                    addTrackingLineForm.get("quantity")?.errors?.["max"]
                  ) {
                    {{
                      "common.validation.max_value"
                        | translate
                          : {
                              max: addTrackingLineForm.get("quantity")
                                ?.errors?.["max"]?.max,
                            }
                    }}
                  }
                }
              </small>
            </div>
          </div>

          <!-- Second Row: Lot, Expiry, Serial (lot/expiry only when category/profile requires them) -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <!-- Lot Number -->
            @if (isFormFieldRequired("lot")) {
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ "inbound.fields.lot" | translate }}
                <span class="text-red-500">*</span>
              </label>
              <p-select
                data-testid="receive-goods-lot-select"
                [options]="getLotOptionsWithAdd()"
                formControlName="lot"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'inbound.receive.select_lot' | translate"
                styleClass="w-full"
                [class.p-invalid]="
                  addTrackingLineForm.get('lot')?.invalid &&
                  addTrackingLineForm.get('lot')?.touched
                "
                (onChange)="onFormLotChange($event)"
                (onClear)="onFormLotCleared()"
                [showClear]="true"
              >
                <ng-template let-option pTemplate="item">
                  @if (option.isAddOption) {
                    <div
                      class="flex items-center gap-2 p-2 text-blue-600 font-medium"
                    >
                      <i class="pi pi-plus"></i>
                      <span>{{ option.label }}</span>
                    </div>
                  } @else {
                    <div>{{ option.label }}</div>
                  }
                </ng-template>
              </p-select>
              <small
                class="text-red-500 text-sm mt-1 block"
                [class.invisible]="
                  !(
                    addTrackingLineForm.get('lot')?.invalid &&
                    addTrackingLineForm.get('lot')?.touched
                  )
                "
                [style.min-height.rem]="1.25"
              >
                {{ "common.validation.required" | translate }}
              </small>
            </div>
            }

            <!-- Expiry Date -->
            @if (isFormFieldRequired("expiry")) {
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ "inbound.fields.expiry" | translate }}
                <span class="text-red-500">*</span>
              </label>
              <app-advanced-datepicker
                data-testid="receive-goods-expiry-date-input"
                formControlName="expiryDate"
                mode="single"
                [showTime]="false"
                [dateFormat]="'DD/MM/YYYY'"
                [placeholder]="'inbound.fields.expiry' | translate"
                [minDate]="minDate"
                [class]="
                  'w-full ' +
                  (addTrackingLineForm.get('expiryDate')?.invalid &&
                  addTrackingLineForm.get('expiryDate')?.touched
                    ? 'show-error-styling'
                    : '')
                "
              ></app-advanced-datepicker>
              <small
                class="text-red-500 text-sm mt-1 block"
                [class.invisible]="
                  !(
                    addTrackingLineForm.get('expiryDate')?.invalid &&
                    addTrackingLineForm.get('expiryDate')?.touched
                  )
                "
                [style.min-height.rem]="1.25"
              >
                {{ "common.validation.required" | translate }}
              </small>
            </div>
            }

            <!-- Serial Number -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ "inbound.fields.serial" | translate }}
                @if (isFormFieldRequired("serial")) {
                  <span class="text-red-500">*</span>
                }
              </label>
              <app-tag-input
                data-testid="receive-goods-serial-input"
                formControlName="serial"
                [placeholder]="'inbound.fields.serial' | translate"
                separator=" "
                [addOnBlur]="true"
                [class.p-invalid]="
                  addTrackingLineForm.get('serial')?.invalid &&
                  addTrackingLineForm.get('serial')?.touched
                "
              ></app-tag-input>
              <small
                class="text-red-500 text-sm mt-1 block"
                [class.invisible]="
                  !(
                    addTrackingLineForm.get('serial')?.invalid &&
                    addTrackingLineForm.get('serial')?.touched
                  )
                "
                [style.min-height.rem]="1.25"
              >
                {{ "common.validation.required" | translate }}
              </small>
            </div>
          </div>

          <!-- Third Row: Add Button -->
          <div class="mb-3">
            <div class="flex items-center justify-end gap-2">
              <app-button
                [testId]="'receive-goods-add-tracking-line-btn'"
                [label]="
                  editingTrackingLineId
                    ? ('common.actions.save' | translate)
                    : ('inbound.actions.add' | translate)
                "
                type="info"
                icon="add"
                (emitOnClick)="onAddTrackingLine()"
              ></app-button>
              @if (editingTrackingLineId) {
                <app-button
                  [testId]="'receive-goods-cancel-edit-btn'"
                  [label]="'common.actions.cancel' | translate"
                  type="secondary"
                  [outlined]="true"
                  (emitOnClick)="onCancelEdit()"
                ></app-button>
              }
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Tracking Lines Table -->
  <div class="mb-4 pt-4 border-t border-gray-200">
    <h3 class="text-lg font-semibold text-gray-800 mb-3">
      {{ "inbound.dialog.tracking_lines" | translate }}
      @if (trackingLineGroups.length > 0) {
        <span class="text-sm font-normal text-gray-500">
          ({{ trackingLines.length }})
        </span>
      }
    </h3>

    @if (trackingLineGroups.length === 0) {
      <div class="p-8 text-center bg-gray-50 border border-gray-200 rounded-lg">
        <i class="pi pi-inbox text-4xl text-gray-400 mb-3"></i>
        <p class="text-gray-500">
          {{ "inbound.dialog.no_tracking_lines" | translate }}
        </p>
        <p class="text-sm text-gray-400 mt-2">
          {{ "inbound.dialog.scan_barcode_to_add" | translate }}
        </p>
      </div>
    } @else {
      <!-- Use app-data-table component -->
      <app-data-table
        data-testid="receive-goods-tracking-lines-table"
        [columns]="tableColumns"
        [dataSource]="tableDataSource"
        [loading]="false"
        [hasCheckColumn]="false"
        [hasSerialColumn]="false"
        [totalRecords]="tableDataSource.length"
        [first]="0"
        [rows]="tableDataSource.length"
      >
        <!-- Custom template for group headers and actions -->
        <ng-template
          #customTemplate
          let-value
          let-rowData="rowData"
          let-column="column"
        >
          @if (rowData.isGroupHeader) {
            @if (column.prop === "skuName") {
              <div class="flex items-center gap-2 text-left">
                <i
                  class="pi cursor-pointer flex-shrink-0"
                  [class.pi-chevron-down]="rowData.isExpanded"
                  [class.pi-chevron-right]="!rowData.isExpanded"
                  (click)="toggleSkuGroup(rowData.skuCode)"
                ></i>
                <div class="flex flex-col items-start text-left">
                  <span class="text-gray-800">{{ rowData.skuCode }}</span>
                  <span class="text-sm text-gray-600">{{
                    rowData.skuName
                  }}</span>
                </div>
              </div>
            } @else if (column.prop === "lot") {
              <span>-</span>
            } @else if (column.prop === "expiry") {
              <span>-</span>
            } @else if (column.prop === "serial") {
              <span>-</span>
            } @else if (column.prop === "quantity") {
              <span class="text-right">{{ rowData.totalQuantity }}</span>
            } @else if (column.prop === "plannedQty") {
              <span class="text-right">{{ rowData.plannedQty || "" }}</span>
            } @else if (column.prop === "receivedQty") {
              <span class="text-right">{{ rowData.receivedQty || "" }}</span>
            } @else if (column.prop === "action") {
              <div class="flex justify-end pl-3 action-cell">
                <span
                  [attr.data-testid]="'receive-goods-action-delete-group-' + rowData.skuCode"
                  (click)="removeSkuGroup(rowData.skuCode)"
                  class="action-link action-delete cursor-pointer"
                >
                  {{ "common.actions.delete" | translate }}
                </span>
              </div>
            } @else {
              <span>{{ value }}</span>
            }
          } @else {
            @if (column.prop === "skuName") {
              @if (rowData.isSubRow) {
                <!-- Empty for sub-rows, but keep same structure for alignment -->
                <span
                  [class.editing-row-marker]="rowData.isEditing"
                  [attr.data-editing]="rowData.isEditing ? 'true' : null"
                ></span>
              } @else {
                <div class="flex flex-col items-start text-left">
                  <span class="text-gray-800">{{ rowData.skuCode }}</span>
                  <span class="text-sm text-gray-600">{{
                    rowData.skuName
                  }}</span>
                </div>
              }
            } @else if (column.prop === "lot") {
              <!-- Use same structure as group header for alignment -->
              <span class="text-left">{{ rowData.lot || "" }}</span>
            } @else if (column.prop === "expiry") {
              <!-- Use same structure as group header for alignment -->
              <span class="text-left">{{
                rowData.expiry || rowData.expiryDate
                  ? (rowData.expiryDate | date: "dd/MM/yyyy")
                  : ""
              }}</span>
            } @else if (column.prop === "serial") {
              <!-- Use same structure as group header for alignment -->
              <span class="text-left">{{ rowData.serial || "" }}</span>
            } @else if (column.prop === "quantity") {
              <span class="text-right">{{ rowData.quantity || 0 }}</span>
            } @else if (column.prop === "plannedQty") {
              @if (rowData.isSubRow) {
                <span class="text-right">-</span>
              } @else {
                <span class="text-right">{{ rowData.plannedQty || "" }}</span>
              }
            } @else if (column.prop === "receivedQty") {
              @if (rowData.isSubRow) {
                <span class="text-right">-</span>
              } @else {
                <span class="text-right">{{ rowData.receivedQty || "" }}</span>
              }
            } @else if (column.prop === "action") {
              @if (rowData.isEditing) {
                <div class="flex justify-end action-cell">
                  <span class="text-gray-400 text-sm"></span>
                </div>
              } @else {
                <div class="flex justify-end gap-2 pl-3 action-cell">
                  <span
                    [attr.data-testid]="'receive-goods-action-edit-' + rowData.id"
                    (click)="onEditTrackingLine(rowData)"
                    class="action-link action-edit cursor-pointer"
                  >
                    {{ "common.actions.edit" | translate }}
                  </span>
                  <span
                    [attr.data-testid]="'receive-goods-action-delete-' + rowData.id"
                    (click)="removeTrackingLine(rowData)"
                    class="action-link action-delete cursor-pointer"
                  >
                    {{ "common.actions.delete" | translate }}
                  </span>
                </div>
              }
            } @else {
              <span>{{ value }}</span>
            }
          }
        </ng-template>
      </app-data-table>
    }
  </div>
</div>

<!-- Toast Messages -->
<p-toast data-testid="receive-goods-toast"></p-toast>

<!-- Add Lot Dialog -->
<app-add-lot-dialog
  data-testid="receive-goods-add-lot-dialog"
  [(visible)]="showAddLotDialog"
  [skuCode]="selectedSkuForLot?.skuCode || null"
  [skuName]="selectedSkuForLot?.skuName || null"
  (confirm)="onAddLotConfirm($event)"
  (visibleChange)="showAddLotDialog = $event"
></app-add-lot-dialog>

<!-- Receipt Discrepancy Dialog (required for purchase-order warrants) -->
<p-dialog
  data-testid="receive-goods-discrepancy-dialog"
  [(visible)]="showDiscrepancyDialog"
  [modal]="true"
  [closable]="true"
  [style]="{ width: '44rem' }"
  [header]="'inbound.receive.discrepancy.title' | translate"
>
  <p class="mb-3 text-sm text-gray-600">
    {{ "inbound.receive.discrepancy.description" | translate }}
  </p>
  <p-table [value]="discrepancyEntries" styleClass="w-full">
    <ng-template pTemplate="header">
      <tr>
        <th>{{ "inbound.receive.discrepancy.sku" | translate }}</th>
        <th class="text-right">{{ "inbound.receive.discrepancy.expected" | translate }}</th>
        <th class="text-right">{{ "inbound.receive.discrepancy.received" | translate }}</th>
        <th class="text-right">{{ "inbound.receive.discrepancy.variance" | translate }}</th>
        <th>{{ "inbound.receive.discrepancy.reason" | translate }} *</th>
        <th>{{ "inbound.receive.discrepancy.note" | translate }}</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-entry>
      <tr>
        <td>
          <div class="font-medium">{{ entry.skuCode }}</div>
          <div class="text-xs text-gray-500">{{ entry.skuName }}</div>
        </td>
        <td class="text-right">{{ entry.expectedQty }}</td>
        <td class="text-right">{{ entry.receivedQty }}</td>
        <td class="text-right">{{ entry.receivedQty - entry.expectedQty }}</td>
        <td>
          <input
            pInputText
            class="w-full"
            [(ngModel)]="entry.reason"
            [placeholder]="'inbound.receive.discrepancy.reason_placeholder' | translate"
            [attr.data-testid]="'discrepancy-reason-' + entry.skuCode"
          />
        </td>
        <td>
          <input
            pInputText
            class="w-full"
            [(ngModel)]="entry.note"
            [attr.data-testid]="'discrepancy-note-' + entry.skuCode"
          />
        </td>
      </tr>
    </ng-template>
  </p-table>
  <ng-template pTemplate="footer">
    <p-button
      [label]="'common.actions.cancel' | translate"
      severity="secondary"
      [outlined]="true"
      (onClick)="showDiscrepancyDialog = false"
    />
    <p-button
      [label]="'common.actions.confirm' | translate"
      severity="info"
      [disabled]="!allDiscrepancyReasonsFilled"
      (onClick)="confirmDiscrepancyAndSubmit()"
    />
  </ng-template>
</p-dialog>
